import type { MemberResource } from './api';

/**
 * How (or whether) a member resource can be shown in-app.
 *
 * Lives apart from the viewer component on purpose: BOTH the Resources page
 * (deciding whether to offer a View button) and the viewer dialog (deciding
 * what to render) read this, so the button can never offer a preview the
 * viewer then refuses to display.
 */
export type ResourceRenderer = 'link' | 'pdf' | 'office' | 'download';

/** The formats with no native browser renderer. */
const OFFICE_MIMES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

/**
 * `officeViewerOn` is the tenant's `member_resources_office_viewer` flag. With
 * it off, Office formats fall back to `download`: rendering them means sending
 * a signed link to Microsoft's viewer, which is an explicit opt-in.
 *
 * PDF is never an `office` candidate — it renders in a plain iframe and so
 * never leaves the tenant.
 */
export function resourceRenderer(
  resource: MemberResource,
  officeViewerOn: boolean,
): ResourceRenderer {
  if (resource.kind === 'link') return resource.linkUrl ? 'link' : 'download';
  if (resource.mimeType === 'application/pdf') return 'pdf';
  if (officeViewerOn && resource.mimeType && OFFICE_MIMES.has(resource.mimeType)) return 'office';
  return 'download';
}
